import { FC, ReactNode, useState, useEffect } from "react";
import { AnimatePresence, MotionConfig, motion } from "framer-motion";
import Dynamic from "@/lib/dynamic";

// Core providers that all routes need
import { AppSessionProvider, useAppSession } from "@/contexts/appSession";
import { PermissionsProvider } from "@/contexts/permissions";
import { SocketIOProvider } from "@/contexts/socketIo";
import { SubscriptionsProvider } from "@/contexts/subscription";
import { NotificationContext } from "@/contexts/notifications";
import { useLazyNotificationToastApi } from "@/hooks/useNotificationToastApi";
import { DataStreamEventBusProvider } from "@/contexts/dataStreamEventBusContext";
import { GroupSubscriptionProvider } from "@/contexts/groupSubscription";
import { evictStaleContent } from "@/db/chatDatabase";
import { evictMetricAggregationCache } from "@/db/metricAggregationDatabase";
import { MetricAggregationCacheInvalidator } from "@/components/metrics/MetricAggregationCacheInvalidator";
import { TTSProvider } from "@/tts/TTSContext";
import { getConfigSync } from "@/lib/config";

/**
 * Mounts the TTS pipeline only when the ttsen feature is enabled (TTSEN_ENABLED).
 * When disabled, children render without a provider, so useTTSOptional() returns
 * null and every TTS control hides itself — nothing ever pings ttsen.
 */
const TTSGate: FC<{ children: ReactNode }> = ({ children }) => {
  if (!getConfigSync().ttsEnabled) return <>{children}</>;
  return <TTSProvider>{children}</TTSProvider>;
};

// Lazy load react-toastify (~20KB) — only needed when a notification fires
const ToastContainer = Dynamic(
  () => import("react-toastify").then(mod => ({ default: mod.ToastContainer })),
  { loading: () => <></> }
);

// Loading overlay components
import { Spinner } from "@/components/ui/spinner";
import {
  LOADING_MESSAGES,
  getRandomLoadingMessage,
} from "@/constants/loadingMessages";

/** Minimum time to show the loading overlay (ms) */
const MIN_LOADING_OVERLAY_TIME = 1500;

/** Time before showing a connection error on the loading overlay (ms) */
const LOADING_TIMEOUT = 30000;

// ============================================================================
// APP LOADING OVERLAY
// ============================================================================

/**
 * Full-screen loading overlay shown while app initializes.
 * Fades out when session is initialized and min time has elapsed.
 */
const AppLoadingOverlay: FC = () => {
  const { isInitialized } = useAppSession();
  const [minTimeElapsed, setMinTimeElapsed] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [message, setMessage] = useState(LOADING_MESSAGES[0]);

  const show = !isInitialized || !minTimeElapsed;

  // Minimum display time
  useEffect(() => {
    const timer = setTimeout(
      () => setMinTimeElapsed(true),
      MIN_LOADING_OVERLAY_TIME
    );
    return () => clearTimeout(timer);
  }, []);

  // Timeout — if we're still loading after 30s, show error state
  useEffect(() => {
    if (isInitialized) return;
    const timer = setTimeout(() => setTimedOut(true), LOADING_TIMEOUT);
    return () => clearTimeout(timer);
  }, [isInitialized]);

  // Rotating messages
  useEffect(() => {
    if (show && !timedOut) {
      const interval = setInterval(
        () => setMessage(getRandomLoadingMessage()),
        500
      );
      return () => clearInterval(interval);
    }
  }, [show, timedOut]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="app-loading-overlay"
          initial={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[9999] flex flex-col justify-center items-center bg-zinc-50 dark:bg-zinc-900"
        >
          {timedOut ? (
            <div className="flex flex-col items-center gap-4 text-center px-4">
              <p className="text-lg text-zinc-500 dark:text-zinc-400">
                Unable to connect to the server
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-2 rounded-md font-medium bg-blue-500 hover:bg-blue-600 text-white transition-colors"
              >
                Retry
              </button>
            </div>
          ) : (
            <Spinner size="64px" message={message} />
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// CORE PROVIDERS
// ============================================================================

type CoreProvidersProps = {
  children: ReactNode;
};

/**
 * Core providers that wrap the entire application.
 * These provide authentication, session, socket, and permissions
 * to ALL routes (main app, invite landing, etc.)
 *
 * Provider order:
 * - AppSessionProvider (session state container)
 * - SocketIOProvider (real-time communication, syncs session)
 * - PermissionsProvider (permission checks)
 * - SubscriptionsProvider (subscription state)
 * - NotificationContext (toast notifications)
 * - DataStreamEventBusProvider (streaming events)
 *
 * Also renders:
 * - AppLoadingOverlay (shown during init)
 * - ToastContainer (toast notifications)
 */
export function CoreProviders({ children }: CoreProvidersProps) {
  const notificationApi = useLazyNotificationToastApi();

  // Evict stale Dexie cache entries on app startup (fire-and-forget)
  useEffect(() => {
    evictStaleContent().catch(() => { /* silent */ });
    evictMetricAggregationCache().catch(() => { /* silent */ });
  }, []);

  return (
    // One switch for the whole app: `reducedMotion="user"` makes every framer
    // component honour the OS "reduce motion" setting. Transform and layout
    // animations are applied instantly for those users; opacity still fades,
    // because fading does not cause the motion that setting exists to stop.
    <MotionConfig reducedMotion="user">
      <AppSessionProvider>
        <SocketIOProvider>
          <GroupSubscriptionProvider>
            {/* Single owner of rebuild-driven invalidation + account purging.
                Must sit under AppSessionProvider and GroupSubscriptionProvider. */}
            <MetricAggregationCacheInvalidator />
            <PermissionsProvider>
              <SubscriptionsProvider>
                <NotificationContext.Provider value={notificationApi}>
                  <DataStreamEventBusProvider>
                    <TTSGate>
                      <AppLoadingOverlay />
                      {children}
                      <ToastContainer position="top-center" />
                    </TTSGate>
                  </DataStreamEventBusProvider>
                </NotificationContext.Provider>
              </SubscriptionsProvider>
            </PermissionsProvider>
          </GroupSubscriptionProvider>
        </SocketIOProvider>
      </AppSessionProvider>
    </MotionConfig>
  );
}
